"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.isStudyClientRecord = exports.studyClientNamePattern = exports.isSameStudyClientName = exports.parseStudyClientTitle = exports.formatStudyClientTitle = exports.isStudyClientComplete = exports.STUDY_CLIENT_CIVILITIES = void 0;
/**
 * The name a commercial gives a study: the client's civility, last name and
 * first name, written in ONE canonical form — « M. DUPONT Éric », « Mme LE
 * GALL Anne-Sophie » — so that turning the study into a project can read the
 * client back out of `urban_analysis.title` and fill the client record
 * (owner, 28 Oct. 2026).
 *
 * The form is what makes the read unambiguous: the civility first, the last
 * name in capitals (every word of it), the first name capitalised. A study
 * titled any other way — the office's own studies, those written before — is
 * simply not read (`parseStudyClientTitle` answers `null`).
 *
 * The civility keys are the client record's own `gender` values.
 */
exports.STUDY_CLIENT_CIVILITIES = {
    MAN: 'M.',
    WOMAN: 'Mme',
};
const words = (value) => value.trim().replace(/\s+/g, ' ');
/** « le gall » → « LE GALL ». */
const lastNameOf = (value) => words(value).toLocaleUpperCase('fr-FR');
/** « anne-sophie » → « Anne-Sophie », « d'ARC » → « D'Arc ». */
const firstNameOf = (value) => words(value)
    .toLocaleLowerCase('fr-FR')
    .replace(/(^|[\s'’-])(\p{L})/gu, (_, before, letter) => before + letter.toLocaleUpperCase('fr-FR'));
/** Whether the three parts are there to make a name. */
const isStudyClientComplete = (client) => !!client.civility && words(client.lastName ?? '').length > 0 && words(client.firstName ?? '').length > 0;
exports.isStudyClientComplete = isStudyClientComplete;
/** The study's title, in the canonical form. */
const formatStudyClientTitle = (client) => `${exports.STUDY_CLIENT_CIVILITIES[client.civility]} ${lastNameOf(client.lastName)} ${firstNameOf(client.firstName)}`;
exports.formatStudyClientTitle = formatStudyClientTitle;
const isCapitalised = (word) => /\p{L}/u.test(word) && word === word.toLocaleUpperCase('fr-FR');
/**
 * The client a canonical title names, or `null` for any other title. The last
 * name is the run of words in capitals after the civility; the first name is
 * what follows, and must not be empty.
 */
const parseStudyClientTitle = (title) => {
    const [civilityLabel, ...rest] = words(title ?? '').split(' ');
    const civility = Object.keys(exports.STUDY_CLIENT_CIVILITIES).find((key) => exports.STUDY_CLIENT_CIVILITIES[key] === civilityLabel);
    if (!civility)
        return null;
    const split = rest.findIndex((word) => !isCapitalised(word));
    if (split <= 0)
        return null;
    return {
        civility,
        lastName: rest.slice(0, split).join(' '),
        firstName: rest.slice(split).join(' '),
    };
};
exports.parseStudyClientTitle = parseStudyClientTitle;
/** A name as two spellings of one person share it: no case, accent, hyphen. */
const comparable = (value) => (value ?? '')
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/’/g, "'")
    .replace(/[\s-]+/g, ' ')
    .trim()
    .toLocaleLowerCase('fr-FR');
/** Whether two spellings are one name: no case, accent, hyphen. */
const isSameStudyClientName = (a, b) => comparable(a) === comparable(b);
exports.isSameStudyClientName = isSameStudyClientName;
/**
 * The `ilike` pattern that finds every record `comparable` would call this
 * name: SQL sees accents and hyphens, so « LEVY » misses « Lévy » and « LE GALL »
 * misses « Le-Gall ». A wide net: `isStudyClientRecord` still decides.
 */
const studyClientNamePattern = (name) => `%${name
    .normalize('NFD')
    .replace(/\p{M}/gu, '')
    .replace(/[^\p{L}\p{N}]+/gu, '%')
    .replace(/[aceinouy]/gi, '_')}%`;
exports.studyClientNamePattern = studyClientNamePattern;
/**
 * Whether a client record is the person a study names: same civility, same
 * last and first names, whatever their case, accents or hyphens (« Dupont
 * Eric » is « M. DUPONT Éric »). Companies never are.
 */
const isStudyClientRecord = (record, client) => !record.company &&
    record.gender === client.civility &&
    (0, exports.isSameStudyClientName)(record.last_name, client.lastName) &&
    (0, exports.isSameStudyClientName)(record.first_name, client.firstName);
exports.isStudyClientRecord = isStudyClientRecord;
//# sourceMappingURL=studyClient.js.map